import React, { useState, useContext } from "react";
import { useForm } from "react-hook-form";
import Cities from "../../../components/signup/data/cities.json";
import Provinces from "../../../components/signup/data/provinces.json";
import { useTranslation } from "react-i18next";
import CustomerApi from "../../../api/customer";
import AuthContext from "../../../components/auth-context";
import useAppNavigate from "../../../components/router/use-app-navigate";
import Submit from "../../../components/inputs/submit";

interface RequestForm {
  cellPhone: string;
  email?: string;
  postalCode: string;
  province: string;
  city: string;
  byStreet: string;
  lastAlley: string;
  plaqueNo: string;
  unitNo: string;
  agentCode?: string;
}

const ChangeAddress = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "SignUp.CustomerInfo",
  });
  const context = useContext(AuthContext);
  const navigate = useAppNavigate();
  const c = context.customerInfo;
  console.log("c", context.customerInfo);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<RequestForm>({
    defaultValues: {
      cellPhone: c?.cellPhone || "",
      email: c?.email || "",
      postalCode: c?.postalCode || "",
      province: c?.byStreet || "",
      city: c?.byStreet || "",
      byStreet: c?.byStreet || "",
      lastAlley: c?.lastAlley || "",
      plaqueNo: c?.plaqueNo || "",
      unitNo: c?.unitNo || "",
    },
  });

  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState<any>();
  const onSubmit = (data: RequestForm) => {
    try {
      setIsSubmit(false);
      const customerApi = new CustomerApi(context);
      const payload = {
        ...data,
        address: `${data.byStreet}, ${data.lastAlley} ,پلاک ${data.plaqueNo},واحد ${data.unitNo}`,
        cityReferenceDataId: selectedCity,
      };
      return customerApi.put(payload).then(({ errorMessage }) => {
        setIsSubmit(true);
        if (!errorMessage) {
          navigate("../");
        } else setErrorMessage(errorMessage);
        return errorMessage;
      });
    } catch (e) {
      console.error("Submission failed:", e);
    }
  };

  return (
    c && (
      <form onSubmit={handleSubmit(onSubmit)} className="signup-section">
        {errorMessage && (
          <div className="text-red-500 text-right p-3 mb-4">{errorMessage}</div>
        )}
        <div className="md:w-full lg:w-full xl:w-2/3 mx-auto xs:p-0  mt-10 p-10 rounded-xl">
          <div className="form-section rtl flex flex-col sm:flex-row gap-4">
            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Cell number")} *</label>
              <input
                {...register("cellPhone", {
                  required: t("required"),
                  minLength: { value: 11, message: t("less than 11 digits") },
                })}
                type="tel"
                className="w-full border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your cell number")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.cellPhone?.message}
              </p>
            </div>

            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Email")}</label>
              <input
                {...register("email", {
                  maxLength: {
                    value: 40,
                    message: t("maximum 40 characters allowed"),
                  },
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t("email is not valid"),
                  },
                })}
                type="email"
                className={`w-full border-2 p-4 rounded-lg mt-2 text-left tracking-wider ${
                  errors.email ? "border-red-500" : ""
                }`}
                placeholder={t("enter your email")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.email?.message}
              </p>
            </div>

            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Postal code")} *</label>
              <input
                {...register("postalCode", {
                  required: t("required"),
                  maxLength: { value: 10, message: t("less than 10 digits") },
                })}
                maxLength={10}
                type="tel"
                className="w-full border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your postal code")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.postalCode?.message}
              </p>
            </div>
          </div>

          <div className="form-section rtl flex flex-col sm:flex-row gap-4">
            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Province")} *</label>
              <select
                {...register("province", {
                  required: t("required"),
                })}
                value={selectedProvince}
                className="w-full mt-2"
                onChange={(e) => {
                  setSelectedProvince(e.target.value);
                  setValue("province", e.target.value);
                }}
              >
                <option value="" disabled>
                  {t("choose your province")}
                </option>
                {Provinces.map(({ name, id }) => (
                  <option value={id} key={id}>
                    {name}
                  </option>
                ))}
              </select>
              <p className="absolute left-0 text-red-500">
                {errors.province?.message}
              </p>
            </div>

            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("City")} *</label>
              <select
                {...register("city", {
                  required: t("required"),
                })}
                value={selectedCity}
                className="w-full mt-2"
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setValue("city", e.target.value);
                }}
              >
                <option value="" disabled>
                  {t("choose your city")}
                </option>
                {Cities.filter(
                  ({ provinceId }) => `${provinceId}` === selectedProvince,
                ).map(({ id, name }) => (
                  <option value={id} key={id}>
                    {name}
                  </option>
                ))}
              </select>
              <p className="absolute left-0 text-red-500">
                {errors.city?.message}
              </p>
            </div>

            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Street")} *</label>
              <input
                {...register("byStreet", {
                  required: t("required"),
                  maxLength: {
                    value: 50,
                    message: t("maximum 50 characters allowed"),
                  },
                  value: c.firstName,
                })}
                type="text"
                className="w-full border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your street")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.byStreet?.message}
              </p>
            </div>
          </div>

          <div className="form-section rtl flex flex-wrap gap-4 mb-10">
            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Last alley")} *</label>
              <input
                {...register("lastAlley", {
                  required: t("required"),
                  maxLength: {
                    value: 50,
                    message: t("maximum 50 characters allowed"),
                  },
                })}
                type="text"
                className="w-full border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your last alley")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.lastAlley?.message}
              </p>
            </div>

            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Plaque")} *</label>
              <input
                {...register("plaqueNo", {
                  required: t("required"),
                })}
                type="text"
                className="w-full border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your plaque")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.plaqueNo?.message}
              </p>
            </div>

            <div className="form-group mt-6 relative w-full sm:w-1/3">
              <label className="pr-4">{t("Unit NO")} *</label>
              <input
                {...register("unitNo", {
                  required: t("required"),
                })}
                type="text"
                className="w-full border-2 p-4 rounded-lg mt-2 text-right tracking-wider"
                placeholder={t("enter your unit NO")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.unitNo?.message}
              </p>
            </div>
          </div>
          <Submit loading={isSubmit} />
        </div>
      </form>
    )
  );
};

export default ChangeAddress;
